import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IAiRepository,
  AiDescriptionInput,
  AiImageInput,
  AiImageResult,
  AiBlogInput,
  AiBlogResult,
  AiMessagingReplyInput,
  AiMessagingReplyResult,
  AiMessagingSummaryInput,
  AiMessagingSummaryResult,
  AiSupportChatInput,
  AiSupportChatResult,
  AiSupportDraftInput,
  AiSocialCaptionInput,
  AiSocialCaptionResult,
} from '@erp/data-access/interfaces';
import { functionError } from './function-error';
import { newAiOperationId } from './ai-operation-id';

/** Wire format of `ai-generate` history turns: customer = the "user" the
 *  model replies to, merchant = the "assistant" voice it writes as. */
function mapMessagingHistory(
  history: AiMessagingReplyInput['history'],
): Array<{ role: 'user' | 'assistant'; content: string }> {
  return history.map((turn) => ({
    role: turn.role === 'customer' ? 'user' : 'assistant',
    content: turn.content,
  }));
}

/**
 * AI content generation — thin proxy to the `ai-generate` Edge Function,
 * which holds the OpenAI API key server-side. Mirrors the invitations
 * repository's invoke + error-surfacing pattern: the function returns JSON
 * like `{ error: "code", detail: "…" }` on failure, which we re-throw so the
 * UI can show a meaningful message (e.g. quota exhausted, org not verified).
 */
export class SupabaseAiRepository implements IAiRepository {
  constructor(private supabase: SupabaseClient) {}

  async generateDescription(input: AiDescriptionInput): Promise<string> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: 'description',
          name: input.name,
          language: input.language,
          category: input.category,
          brand: input.brand,
          keywords: input.keywords,
          tone: input.tone,
          existingText: input.existingText,
          action: input.action,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_description_failed');
    const text = (data as { text?: string } | null)?.text?.trim();
    if (!text) throw new Error('ai_empty_response');
    return text;
  }

  async supportChat(input: AiSupportChatInput): Promise<AiSupportChatResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          mode: 'support-chat',
          history: input.history,
          language: input.language,
          tenantName: input.tenantName,
          plan: input.plan,
          ticketType: input.ticketType,
          hasAttachment: input.hasAttachment ?? false,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_support_failed');
    const res = data as {
      reply?: string;
      escalate?: boolean;
      type?: string;
      title?: string;
    } | null;
    const reply = res?.reply?.trim();
    if (!reply) throw new Error('ai_empty_response');
    const allowed = ['support', 'bug', 'feature', 'feedback'] as const;
    const type = (allowed as readonly string[]).includes(res?.type ?? '')
      ? (res?.type as AiSupportChatResult['type'])
      : 'support';
    return {
      reply,
      escalate: res?.escalate === true,
      type,
      title: res?.title?.trim() ?? '',
    };
  }

  async supportDraftReply(input: AiSupportDraftInput): Promise<string> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          mode: 'support-draft',
          history: input.history,
          ticketType: input.ticketType,
          ticketTitle: input.ticketTitle,
          tenantName: input.tenantName,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_draft_failed');
    const draft = (data as { draft?: string } | null)?.draft?.trim();
    if (!draft) throw new Error('ai_empty_response');
    return draft;
  }

  async draftMessagingReply(
    input: AiMessagingReplyInput,
  ): Promise<AiMessagingReplyResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: 'messaging-draft',
          history: mapMessagingHistory(input.history),
          companyName: input.companyName,
          clientName: input.clientName,
          instruction: input.instruction,
          existingText: input.existingText,
          action: input.action,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_messaging_draft_failed');
    const res = data as {
      text?: string;
      costCredits?: number;
      balanceCredits?: number;
    } | null;
    const text = res?.text?.trim();
    if (!text) throw new Error('ai_empty_response');
    return {
      text,
      costCredits: res?.costCredits ?? null,
      balanceCredits: res?.balanceCredits ?? null,
    };
  }

  async summarizeMessagingConversation(
    input: AiMessagingSummaryInput,
  ): Promise<AiMessagingSummaryResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: 'messaging-summary',
          history: mapMessagingHistory(input.history),
          language: input.language,
          clientName: input.clientName,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_messaging_summary_failed');
    const res = data as {
      summary?: string;
      costCredits?: number;
      balanceCredits?: number;
    } | null;
    const summary = res?.summary?.trim();
    if (!summary) throw new Error('ai_empty_response');
    return {
      summary,
      costCredits: res?.costCredits ?? null,
      balanceCredits: res?.balanceCredits ?? null,
    };
  }

  async generateImage(input: AiImageInput): Promise<AiImageResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: input.mode === 'edit' ? 'image-edit' : 'image-generate',
          prompt: input.prompt,
          sourceImageUrl: input.sourceImageUrl,
          imageStyle: input.style ?? 'product',
        },
      },
    );
    if (error) throw await functionError(error, 'ai_image_failed');
    const res = data as {
      base64?: string;
      mimeType?: string;
      costCredits?: number;
      balanceCredits?: number;
    } | null;
    if (!res?.base64) throw new Error('ai_empty_response');
    return {
      base64: res.base64,
      mimeType: res.mimeType ?? 'image/png',
      costCredits: res.costCredits ?? null,
      balanceCredits: res.balanceCredits ?? null,
    };
  }

  async generateBlog(input: AiBlogInput): Promise<AiBlogResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: 'blog',
          topic: input.topic,
          language: input.language,
          keywords: input.keywords,
          tone: input.tone,
        },
      },
    );
    if (error) throw await functionError(error, 'ai_blog_failed');
    const res = data as {
      title?: string;
      excerpt?: string;
      tag?: string;
      content?: string;
      costCredits?: number;
      balanceCredits?: number;
    } | null;
    const title = res?.title?.trim();
    const content = res?.content?.trim();
    if (!title || !content) throw new Error('ai_empty_response');
    return {
      title,
      excerpt: res?.excerpt?.trim() ?? '',
      tag: res?.tag?.trim() ?? '',
      content,
      costCredits: res?.costCredits ?? null,
      balanceCredits: res?.balanceCredits ?? null,
    };
  }

  async generateSocialCaptions(
    input: AiSocialCaptionInput,
  ): Promise<AiSocialCaptionResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'ai-generate',
      {
        body: {
          operationId: newAiOperationId(),
          mode: 'social-caption',
          topic: input.topic,
          language: input.language,
          link: input.link,
          tone: input.tone,
          platforms: input.platforms ?? ['facebook', 'instagram'],
        },
      },
    );
    if (error) throw await functionError(error, 'ai_caption_failed');
    const res = data as {
      facebook?: string;
      instagram?: string;
      costCredits?: number;
      balanceCredits?: number;
    } | null;
    const facebook = res?.facebook?.trim() ?? '';
    const instagram = res?.instagram?.trim() ?? '';
    if (!facebook && !instagram) throw new Error('ai_empty_response');
    return {
      facebook,
      instagram,
      costCredits: res?.costCredits ?? null,
      balanceCredits: res?.balanceCredits ?? null,
    };
  }
}
